import { useEffect, useRef, type ReactNode } from "react";
import { animate, useReducedMotion } from "../../lib/motion.js";
import { cn } from "../../lib/utils.js";
import type { WhatsAppWorkspaceTab } from "./whatsapp-workspace-tab-state.js";

export function WhatsAppWorkspaceTabPanel({
  tab,
  activeTab,
  labelledBy,
  children,
  className,
}: {
  tab: WhatsAppWorkspaceTab;
  activeTab: WhatsAppWorkspaceTab;
  labelledBy: string;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  const hidden = activeTab !== tab;
  const reduceMotion = useReducedMotion();
  const innerRef = useRef<HTMLDivElement>(null);
  const wasHiddenRef = useRef(hidden);

  useEffect(() => {
    const becameVisible = wasHiddenRef.current && !hidden;
    wasHiddenRef.current = hidden;
    const node = innerRef.current;
    if (!becameVisible || !node) return;

    if (reduceMotion) {
      node.style.opacity = "1";
      node.style.transform = "none";
      return;
    }

    const controls = animate(
      node,
      { opacity: [0, 1], transform: ["translateY(4px)", "translateY(0px)"] },
      { duration: 0.16, ease: "easeOut" },
    );
    return () => {
      controls.stop();
    };
  }, [hidden, reduceMotion]);

  return (
    <div
      id={`whatsapp-tabpanel-${tab}`}
      role="tabpanel"
      aria-labelledby={labelledBy}
      hidden={hidden}
      aria-hidden={hidden}
      className={cn("min-w-0", className)}
      data-tab={tab}
      data-active={hidden ? "false" : "true"}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
